import { useState, useRef, useEffect } from 'react';
import { SmilePlus, Plus, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const _initials = (name) => (name || '?').split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase() || '').join('') || '?';

// Réactions ultra-rapides — les plus utilisées, toujours visibles inline
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '🙌', '🔥', '👀', '💡'];

// Palette étendue accessible via le bouton "+"
export const EXTENDED_REACTIONS = [
    '👍', '👎', '❤️', '💛', '💚', '💙', '💜', '🖤',
    '😂', '🤣', '😅', '😊', '😍', '😘', '🤔', '😎',
    '🎉', '🎊', '🥳', '🙌', '👏', '🤝', '💪', '🙏',
    '🔥', '⚡', '✨', '💯', '💡', '💰', '🚀', '⭐',
    '👀', '👂', '🧠', '❤️‍🔥', '💥', '🎯', '📌', '✅',
    '❌', '⚠️', '❓', '❗', '🆗', '🆒', '🔔', '💬',
];

/**
 * Barre de réactions rapides "one-tap".
 * Affiche les emojis QUICK_REACTIONS directement inline (aucun menu à ouvrir).
 * Le bouton "+" révèle la palette étendue au besoin.
 *  - `reactions` : { [emoji]: [user_id...] }
 *  - `currentUserId` : pour marquer "mine" quand l'utilisateur a réagi
 *  - `onToggle(emoji)` : callback quand on clique un emoji
 *  - `compact` : version plus petite pour les commentaires
 *  - `showQuickAlways` : si `true`, affiche toujours la ligne de quick reactions.
 *      Sinon (défaut sur commentaires), la ligne se révèle au survol du parent
 *      via la classe `group-hover/...` — permet de rester léger visuellement.
 */
export function QuickReactionBar({
    reactions, currentUserId, onToggle, compact = false,
    showQuickAlways = true, hoverGroup, testIdPrefix = 'react',
    members = null,
}) {
    const [showExtended, setShowExtended] = useState(false);
    const [openWho, setOpenWho] = useState(null); // emoji dont on affiche les auteurs
    const rootRef = useRef(null);

    // Ferme le picker étendu au clic extérieur
    useEffect(() => {
        if (!showExtended && !openWho) return;
        const onDoc = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setShowExtended(false);
                setOpenWho(null);
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [showExtended, openWho]);

    // Résolution nom/avatar depuis la liste des membres passée par le parent
    const membersById = (() => {
        if (!members || !Array.isArray(members)) return {};
        const map = {};
        for (const m of members) {
            if (!m) continue;
            const id = m.id || m.user_id;
            if (!id) continue;
            map[id] = {
                name: m.full_name || m.name || m.email || 'Utilisateur',
                avatar: m.avatar_url || m.picture || null,
            };
        }
        return map;
    })();

    const entries = Object.entries(reactions || {}).filter(([, list]) => (list || []).length > 0);
    const activeEmojis = new Set(entries.map(([e]) => e));

    const size = compact ? 'text-sm' : 'text-base';
    const chipSize = compact ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-0.5';
    const btnSize = compact ? 'w-6 h-6 text-sm' : 'w-7 h-7 text-base';

    return (
        <div ref={rootRef} className={cn('relative', compact ? 'mt-1' : 'mt-2')}>
            {/* Compteurs des réactions existantes */}
            {entries.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1 items-center">
                    {entries.map(([emoji, users]) => {
                        const mine = (users || []).includes(currentUserId);
                        const reactors = (users || []).map((uid) => membersById[uid] || { name: uid === currentUserId ? 'Vous' : 'Utilisateur', avatar: null });
                        return (
                            <div
                                key={emoji}
                                className="relative"
                                onPointerEnter={(e) => { if (e.pointerType === 'mouse') setOpenWho(emoji); }}
                                onPointerLeave={(e) => { if (e.pointerType === 'mouse') setOpenWho((prev) => (prev === emoji ? null : prev)); }}
                            >
                                <button
                                    type="button"
                                    onClick={() => onToggle(emoji)}
                                    onContextMenu={(e) => { e.preventDefault(); setOpenWho((prev) => (prev === emoji ? null : emoji)); }}
                                    className={cn(
                                        'rounded-full border flex items-center gap-1 transition-colors',
                                        chipSize,
                                        mine ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                                             : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200',
                                    )}
                                    data-testid={`${testIdPrefix}-existing-${emoji}`}
                                    title={mine ? 'Cliquez pour retirer votre réaction · Passez la souris pour voir qui a réagi' : 'Cliquez pour ajouter votre réaction · Passez la souris pour voir qui a réagi'}
                                >
                                    <span>{emoji}</span>
                                    <span className="font-medium">{users.length}</span>
                                </button>
                                {/* Popover "qui a réagi" — hover desktop, tap-hold ou long-press mobile via clic droit */}
                                {openWho === emoji && reactors.length > 0 && (
                                    <div
                                        data-testid={`${testIdPrefix}-who-${emoji}`}
                                        className="absolute z-40 bottom-full mb-1 left-0 min-w-[200px] max-w-[280px] max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
                                    >
                                        <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1">
                                            <Users className="w-3 h-3" /> A réagi avec {emoji}
                                        </p>
                                        {reactors.map((r, i) => (
                                            <div key={i} className="flex items-center gap-2 px-1 py-1 text-xs text-slate-700">
                                                <Avatar className="h-6 w-6 shrink-0">
                                                    {r.avatar && <AvatarImage src={r.avatar} />}
                                                    <AvatarFallback className="text-[9px] bg-slate-100 text-slate-600">{_initials(r.name)}</AvatarFallback>
                                                </Avatar>
                                                <span className="flex-1 truncate">{r.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {/* Petit indicateur "voir qui" toujours visible pour touch — bouton discret */}
                    <button
                        type="button"
                        onClick={() => setOpenWho(openWho ? null : (entries[0]?.[0] || null))}
                        className="text-slate-300 hover:text-purple-600 transition-colors p-0.5"
                        aria-label="Voir qui a réagi"
                        title="Voir qui a réagi"
                        data-testid={`${testIdPrefix}-who-toggle`}
                    >
                        <Users className="w-3 h-3" />
                    </button>
                </div>
            )}

            {/* Quick reactions inline — un tap = réagi. Aucune ouverture de menu. */}
            <div
                className={cn(
                    'flex items-center gap-0.5 flex-wrap',
                    !showQuickAlways && hoverGroup && `opacity-0 ${hoverGroup} transition-opacity`,
                )}
            >
                {QUICK_REACTIONS.map((e) => {
                    const mine = activeEmojis.has(e) && (reactions?.[e] || []).includes(currentUserId);
                    return (
                        <button
                            key={e}
                            type="button"
                            onClick={() => onToggle(e)}
                            className={cn(
                                'rounded-full flex items-center justify-center transition-all',
                                btnSize, size,
                                mine ? 'bg-indigo-100 ring-1 ring-indigo-300'
                                     : 'hover:bg-slate-100 opacity-70 hover:opacity-100 hover:scale-110',
                            )}
                            data-testid={`${testIdPrefix}-quick-${e}`}
                            aria-label={`Réagir avec ${e}`}
                            title={`Réagir avec ${e}`}
                        >
                            <span>{e}</span>
                        </button>
                    );
                })}
                <button
                    type="button"
                    onClick={() => setShowExtended((v) => !v)}
                    className={cn(
                        'rounded-full flex items-center justify-center transition-colors',
                        btnSize,
                        showExtended ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-slate-100 text-slate-400',
                    )}
                    data-testid={`${testIdPrefix}-toggle-extended`}
                    aria-label="Plus d'emojis"
                    title="Plus d'emojis"
                >
                    {showExtended ? <SmilePlus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                </button>
            </div>

            {/* Palette étendue en popover */}
            {showExtended && (
                <div
                    className="absolute z-30 mt-1 top-full left-0 bg-white rounded-xl shadow-lg border border-slate-200 p-2 grid grid-cols-8 gap-1 w-[280px]"
                    data-testid={`${testIdPrefix}-extended-picker`}
                >
                    {EXTENDED_REACTIONS.map((e) => (
                        <button
                            key={e}
                            type="button"
                            onClick={() => { onToggle(e); setShowExtended(false); }}
                            className="text-lg hover:bg-slate-100 rounded-md p-1 transition-transform hover:scale-110"
                            data-testid={`${testIdPrefix}-ext-${e}`}
                            aria-label={`Réagir avec ${e}`}
                        >
                            {e}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
